<script lang="ts">
	import { onDestroy } from 'svelte'
	import GoogleSdk from './GoogleSdk.svelte'
	import Placeholder from './Placeholder.svelte'
	// @ts-ignore
	import { PUBLIC_GOOGLE_PLACES_API_KEY } from '$env/static/public'
	import authState from '$lib/stores/auth-state'
	import { getContextClient, queryStore } from '@urql/svelte'
	import {
		MapViewDocument,
		type MapViewQuery,
		type MapViewQueryVariables,
	} from '$src/lib/queries/generated/QueryMapView'
	import { browser } from '$app/environment'
	import { urlState } from '$lib/stores/url-state'
	import { BlockType } from '$lib/graphql/enums'
	import { isMapLoaded, mapStore } from '$lib/stores/map-state'
	import { createMap, setMobileSettings, setupMapListeners } from '$lib/utils/map'
	import MapMarkers from './MapMarkers.svelte'
	import CustomMapItems from './CustomMapItems.svelte'
	import blockState from '$lib/stores/block-state'

	// const MAX_ZOOM_LEVEL = 17
	const DEFAULT_ZOOM_LEVEL = 15
	let mapElement: HTMLDivElement = $state(null)

	const client = getContextClient()

	let { tripId, isPublic, itineraryId, requiresAuthentication } = $derived($urlState)

	let signedIn = $derived($authState.signedIn)

	let blockStore = $derived(
		queryStore<MapViewQuery, MapViewQueryVariables>({
			client,
			query: MapViewDocument,
			variables: {
				id: tripId,
				isPublic,
				...(itineraryId ? { itineraryId } : {}),
				...($blockState.listingIds.length > 0 && !tripId && isPublic
					? { listingIds: $blockState?.listingIds }
					: {}),
			},
			pause: !browser || (requiresAuthentication && !signedIn) || !blockState,
		}),
	)

	let blocks = $derived($blockStore?.data?.mapView ?? [])
	let markerBlocks = $derived(
		blocks?.filter((b) => b.point != null && !b.isOrigin && !b.isDestination) ?? [],
	)
	let destinationBlocks = $derived(
		blocks?.filter(
			(b) => b.blockType === BlockType.Destination && !b.isOrigin && !b.isDestination,
		) ?? [],
	)
	let customRegionBlocks = $derived(
		blocks?.filter((b) => b.blockType === BlockType.CustomRegion || b.customRegion != null) ?? [],
	)

	let cleanupListeners = () => {}
	async function initialize(sdk: typeof google) {
		if (!mapElement || $isMapLoaded) return

		const map = createMap(mapElement, sdk)

		// Set initial zoom in store
		mapStore.dispatch({ type: 'SET_ZOOM', payload: map.getZoom() || DEFAULT_ZOOM_LEVEL })
		mapStore.dispatch({ type: 'SET_MAP', payload: map })
		mapStore.dispatch({ type: 'SET_LOADED', payload: true })

		setMobileSettings()
		setupMapListeners(map)
		cleanupListeners = setupMapListeners(map)
	}

	onDestroy(() => {
		const map = $mapStore.map

		cleanupListeners()
		if (map) {
			google.maps.event.clearInstanceListeners(map)
		}

		mapStore.dispatch({ type: 'RESET' })
	})
</script>

<GoogleSdk apiKey={PUBLIC_GOOGLE_PLACES_API_KEY} onready={initialize} />

<div class="w-full h-full z-10" bind:this={mapElement} data-testid="map-container">
	<!-- Map Container -->
</div>

<div class="w-full h-full overflow-hidden z-20">
	{#if $isMapLoaded && ((requiresAuthentication && signedIn) || !requiresAuthentication) && !$blockStore?.fetching}
		<MapMarkers {markerBlocks} allBlocks={blocks} isEmptyState={destinationBlocks.length === 0} />

		<CustomMapItems {customRegionBlocks} isEmptyState={destinationBlocks.length === 0} />
	{/if}

	{#if !$isMapLoaded || (requiresAuthentication && !signedIn)}
		<div class="absolute inset-0 z-10 overflow-clip">
			<Placeholder class="w-full h-full" />
		</div>
	{/if}
</div>

<style>
	:global(.gm-style .gmnoprint:not(.gm-bundled-control)) {
		bottom: 10px !important;
	}

	:global(.gm-style .gm-style-cc) {
		bottom: 10px !important;
	}
</style>
